import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { MultiColorIonoff } from '../ui/MultiColorIonoff';
import { X, Plus, Minus, Pencil, Check, XCircle, ChevronLeft, ChevronDown, ChevronUp, Eye, EyeOff } from 'lucide-react';
import { useProdMode } from '../../hooks/useProdMode';
import { useDevModeEasterEgg } from '../../hooks/useDevModeEasterEgg';
import { Conversation } from '../../types';
import { useHighlighting } from '../../hooks/useHighlighting';
import { MajorityJudgmentLeaderboard } from './MajorityJudgmentLeaderboard';
import { SelfEvalLeaderboard } from './SelfEvalLeaderboard';
import { ConversationSearch } from './ConversationSearch';
import { 
  useAllModelMajorityJudgments, 
  useConversationModelMajorityJudgments,
  useGlobalModelMajorityJudgments,
  useConversationCreatorMajorityJudgments,
  useUserCreatorMajorityJudgments,
  useGlobalCreatorMajorityJudgments,
  useConversationSelfEvalMajorityJudgments,
  useSelfEvalMajorityJudgments,
  useGlobalSelfEvalMajorityJudgments
} from '../../hooks/useMajorityJudgment';
import { useTop10Models } from '../../hooks/useTop10Models';
import { useQuestionMarks } from '../../hooks/useQuestionMarks';
import { useInteractedControls } from '../../hooks/useInteractedControls';
import { HelpTooltip, HoverHelpTooltip } from '../ui/HelpTooltip';
import { db } from '../../firebase/config';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';

interface ChatSidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  conversations: Conversation[];
  publishedConversations?: Conversation[]; // NEW: Published conversations
  currentConversationId?: string;
  onSelectConversation: (conversationId: string) => void;
  onNewConversation: () => void;
  onRenameConversation: (conversationId: string, newTitle: string) => Promise<void>;
  onPublishConversation?: (conversationId: string) => Promise<void>; // NEW: Publish conversation
  onUnpublishConversation?: (conversationId: string) => Promise<void>; // NEW: Unpublish conversation
  onRight2Privacy?: () => Promise<{ conversationsUpdated: number; messagesUpdated: number } | void>; // NEW: Right to privacy - replaces all user content
  highlighting: ReturnType<typeof useHighlighting>;
  tagColors: string[]; // Ordered colors for T models
  modelColorAssignments: Map<string, string>; // Map of modelId -> color hex
  activeModels: string[]; // Model IDs that are currently active in the header
  validOpenRouterModelIds: Set<string>; // Valid OpenRouter model IDs from header search
  width?: number; // Custom width in pixels (defaults to 256px / w-64)
  prioritySetting?: 'free' | 'speed' | 'balance' | 'value' | 'intelligence' | 'custom'; // Current priority setting from slider
  localGradeState?: any; // NEW: Local grade state from useConversation
  messages?: any[]; // NEW: Messages array to parse matrix rankings
  pendingConversationId?: string | null;
  currentUserId?: string; // NEW: Current user ID for "you" display
}

export const ChatSidebar: React.FC<ChatSidebarProps> = ({ 
  isOpen, 
  onToggle,
  conversations,
  publishedConversations = [], // NEW: Published conversations
  currentConversationId,
  onSelectConversation,
  onNewConversation,
  onRenameConversation,
  onPublishConversation, // NEW: Publish conversation
  onUnpublishConversation, // NEW: Unpublish conversation
  onRight2Privacy, // NEW: Right to privacy handler
  highlighting,
  tagColors,
  currentUserId,
  modelColorAssignments,
  activeModels,
  validOpenRouterModelIds,
  width = 256, // Default to 256px (w-64)
  prioritySetting, // Current priority setting
  localGradeState, // NEW: Local grade state from useConversation
  messages, // NEW: Messages array to parse matrix rankings
  pendingConversationId
}) => {
  // Fetch threshold configuration from Firestore
  // Default values used if config not available
  const [thresholds, setThresholds] = useState({
    minJudgmentsConversation: 2,   // N (number of judgments) for conversation-level (both peer-review and self-eval)
    minPromptsUser: 1,              // M (number of prompts) for user-level (both peer-review and self-eval)
    minPromptsGlobal: 1,            // M (number of prompts) for global-level (both peer-review and self-eval)
  });
  
  useEffect(() => {
    console.log('🔧 Setting up threshold configuration listener...');
    // Real-time listener for threshold configuration
    const unsubscribe = onSnapshot(
      doc(db, 'system', 'selfevalGradingModelTop'),
      (docSnapshot: any) => {
        console.log('📡 Threshold snapshot received:', {
          exists: docSnapshot.exists(),
          data: docSnapshot.data()
        });
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          setThresholds({
            minJudgmentsConversation: data.minJudgmentsConversation ?? 2,
            minPromptsUser: data.minPromptsUser ?? 1,  // Use 1 as fallback, matching Firestore default
            minPromptsGlobal: data.minPromptsGlobal ?? 10,  // Use 10 as fallback, matching Firestore default
          });
          console.log('📊 Loaded threshold configuration from Firestore:', {
            minJudgmentsConversation: data.minJudgmentsConversation,
            minPromptsUser: data.minPromptsUser,
            minPromptsGlobal: data.minPromptsGlobal,
          });
        } else {
          console.warn('⚠️ Threshold document does not exist, using defaults');
        }
      },
      (error: any) => {
        console.error('❌ Failed to load threshold configuration:', error);
      }
    );
    
    return () => {
      console.log('🔧 Cleaning up threshold listener');
      unsubscribe();
    };
  }, []);
  
  // Leaderboard view mode: 'creator' (default) or 'model'
  const [leaderboardViewMode, setLeaderboardViewMode] = useState<'creator' | 'model'>('creator');
  
  // 🥚 Easter egg: Developer mode override
  // Click/tap the ionoff logo 8 times to toggle dev mode
  const { devModeOverride, toggleDevModeOverride } = useProdMode();
  const { handleClick: handleLogoClick, handleTouchEnd: handleLogoTouchEnd, clickCount: logoClickCount } = useDevModeEasterEgg(
    useCallback(() => {
      toggleDevModeOverride();
    }, [toggleDevModeOverride])
  );
  const [leaderboardExpanded, setLeaderboardExpanded] = useState<boolean>(false);
  const [chipLimit, setChipLimit] = useState<number | null>(3); // 3 = show first 3, null = show all
  const [recentExpanded, setRecentExpanded] = useState<boolean>(true);
  const [conversationLimit, setConversationLimit] = useState<number | null>(10); // 10 = show first 10, null = show all
  const [publishedExpanded, setPublishedExpanded] = useState<boolean>(true); // Published section expanded by default
  const [publishedLimit, setPublishedLimit] = useState<number | null>(10); // 10 = show first 10, null = show all
  
  // Conversation editing state
  const [editingConversationId, setEditingConversationId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  
  // Right2Privacy confirmation state
  const [showPrivacyConfirm, setShowPrivacyConfirm] = useState(false);
  const [privacyLoading, setPrivacyLoading] = useState(false);

  // Fetch top10 models for current priority (for leaderboard filtering)
  const { isInTop10, creatorIsInTop10 } = useTop10Models(prioritySetting);

  // Question mark help system
  const { getHelpText, hasHelp } = useQuestionMarks();
  const { markInteracted, shouldShowHelp } = useInteractedControls();

  // SELF-EVAL judgments at all three levels (ionoff vs best)
  // Pass messages to parse matrix rankings for perfect alignment
  const conversationSelfEval = useConversationSelfEvalMajorityJudgments(currentConversationId || null, prioritySetting, localGradeState, messages);
  const userSelfEval = useSelfEvalMajorityJudgments(prioritySetting);
  const globalSelfEval = useGlobalSelfEvalMajorityJudgments(prioritySetting);

  // MODEL-LEVEL judgments (when toggled to model view) - filtered by active models
  const conversationModels = useConversationModelMajorityJudgments(
    currentConversationId || null, 
    thresholds.minJudgmentsConversation,
    prioritySetting,
    activeModels,
    localGradeState // NEW: Pass local state
  );
  const userModels = useAllModelMajorityJudgments(thresholds.minPromptsUser, prioritySetting, activeModels);
  const globalModels = useGlobalModelMajorityJudgments(thresholds.minPromptsGlobal, prioritySetting, activeModels);
  
  // CREATOR-LEVEL judgments (default view) - also filtered by priority
  const conversationCreators = useConversationCreatorMajorityJudgments(currentConversationId || null, thresholds.minJudgmentsConversation, prioritySetting, localGradeState);
  const userCreators = useUserCreatorMajorityJudgments(thresholds.minPromptsUser, prioritySetting);
  const globalCreators = useGlobalCreatorMajorityJudgments(thresholds.minPromptsGlobal, prioritySetting);
  
  // Select which data to display based on view mode
  const conversationData = leaderboardViewMode === 'creator' ? conversationCreators : conversationModels;
  const userData = leaderboardViewMode === 'creator' ? userCreators : userModels;
  const globalData = leaderboardViewMode === 'creator' ? globalCreators : globalModels;

  // Helper to check if a leaderboard will have qualified models to display
  const hasQualifiedModels = (data: any[], minThreshold: number, usePromptCount: boolean): boolean => {
    const result = data.some(item => {
      const mj = item.userJudgment || item.globalJudgment;
      if (!mj) {
        console.log('🔍 hasQualifiedModels: No MJ', { item, creatorPrefix: item.creatorPrefix || item.modelId });
        return false;
      }
      
      // Get clear field names
      const judgmentsNumber = mj.judgmentsNumber ?? 0;
      const promptsNumber = mj.promptsNumber ?? 0;
      
      // CRITICAL: judgmentsNumber must be > 0 (actual grades exist)
      if (judgmentsNumber === 0) {
        console.log('🔍 hasQualifiedModels: judgmentsNumber=0', { judgmentsNumber, creatorPrefix: item.creatorPrefix || item.modelId });
        return false;
      }
      
      if (usePromptCount) {
        // For user/global leaderboards: require promptsNumber >= threshold AND judgmentsNumber >= promptsNumber*2
        // This ensures at least 2 judgments per prompt on average
        // (avoids showing leaderboards for users who only use ais=1)
        const meetsThreshold = promptsNumber >= minThreshold;
        const meetsRatio = judgmentsNumber >= promptsNumber * 2;
        const qualified = meetsThreshold && meetsRatio;
        console.log('🔍 hasQualifiedModels: Prompt count check', {
          creatorPrefix: item.creatorPrefix || item.modelId,
          judgmentsNumber,
          promptsNumber,
          minThreshold,
          meetsThreshold,
          meetsRatio,
          qualified
        });
        return qualified;
      } else {
        // For conversation-level: check judgmentsNumber >= threshold
        // CRITICAL FIX: In a single-prompt conversation, promptsNumber=1 but judgmentsNumber can be 2, 3, 6, etc.
        // We need to check judgmentsNumber, NOT promptsNumber!
        const qualified = judgmentsNumber >= minThreshold;
        console.log('🔍 hasQualifiedModels: Judgment count check', {
          creatorPrefix: item.creatorPrefix || item.modelId,
          judgmentsNumber,
          promptsNumber,
          minThreshold,
          qualified
        });
        return qualified;
      }
    });
    console.log('🔍 hasQualifiedModels: Final result', { dataCount: data.length, result, usePromptCount, minThreshold });
    return result;
  };

  // Helper to detect whether ionoff appears in a leaderboard dataset (model or creator view)
  const includesIonoff = (data: any[]): boolean => {
    return data.some(item => {
      if ('modelId' in item) {
        return item.modelId === 'ionoff';
      }
      if ('creatorPrefix' in item) {
        return item.creatorPrefix === 'ionoff';
      }
      return false;
    });
  };

  const ionoffInUserLeaderboard = includesIonoff(userData);
  const ionoffInGlobalLeaderboard = includesIonoff(globalData);
  const shouldHideIonoffInConversationLeaderboard = ionoffInUserLeaderboard || ionoffInGlobalLeaderboard;

  // Debug: Log valid OpenRouter models for comparison
  React.useEffect(() => {
    console.log('🔍 Valid OpenRouter Model IDs:', Array.from(validOpenRouterModelIds).slice(0, 10), `(${validOpenRouterModelIds.size} total)`);
    if (conversationData.length > 0) {
      console.log('🔍 Sample conversation data model IDs:', 
        conversationData.slice(0, 5).map((item: any) => item.modelId || item.creatorPrefix)
      );
    }
  }, [validOpenRouterModelIds, conversationData]);

  // Helper function to normalize model IDs for comparison
  // Removes :online suffix and reasoning suffixes like (high) or (low)
  const normalizeModelId = (modelId: string): string => {
    return modelId
      .replace(/:online$/, '')
      .replace(/ \((high|low)\)$/, '');
  };

  // Helper function to check if a model ID is a valid OpenRouter model
  const isValidOpenRouterModel = (modelId: string): boolean => {
    const normalizedId = normalizeModelId(modelId);
    // Check if the normalized ID exists in validOpenRouterModelIds
    // Also check the raw ID in case it doesn't have suffixes
    return validOpenRouterModelIds.has(normalizedId) || 
           validOpenRouterModelIds.has(modelId) ||
           // Also check if any valid model starts with this ID (for prefix matching)
           Array.from(validOpenRouterModelIds).some(validId => 
             normalizeModelId(validId) === normalizedId
           );
  };

  const filteredConversationData = useMemo(() => {
    const identifiers = leaderboardViewMode === 'creator' ? conversationData.map((c: any) => c.creatorPrefix) : conversationData.map((m: any) => m.modelId);
    console.log('🔍 filteredConversationData: Starting with conversationData', {
      count: conversationData.length,
      leaderboardViewMode,
      identifiers
    });
    
    let filtered = conversationData;

    // Filter out ionoff if it appears in higher-level leaderboards
    if (shouldHideIonoffInConversationLeaderboard) {
      if (leaderboardViewMode === 'creator') {
        filtered = (filtered as typeof conversationCreators).filter(item => 
          item.creatorPrefix !== 'ionoff'
        );
      } else {
        filtered = (filtered as typeof conversationModels).filter(item => 
          item.modelId !== 'ionoff'
        );
      }
    }

    // ALWAYS filter out synthetic ionoff-grading and best IDs (they belong in self-eval only)
    if (leaderboardViewMode === 'creator') {
      filtered = (filtered as typeof conversationCreators).filter(item => 
        item.creatorPrefix && 
        !item.creatorPrefix.startsWith('ionoff-grading') && 
        item.creatorPrefix !== 'best'
      );
    } else {
      filtered = (filtered as typeof conversationModels).filter(item => 
        item.modelId && 
        !item.modelId.startsWith('ionoff-grading') && 
        item.modelId !== 'best'
      );
    }

    // Additionally, only show models that are valid OpenRouter models (choosable from header)
    // This filters out synthetic IDs like ionoff-grading-v..., best, etc.
    if (leaderboardViewMode === 'model') {
      const beforeFilter = (filtered as typeof conversationModels).length;
      filtered = (filtered as typeof conversationModels).filter(item => {
        const isValid = isValidOpenRouterModel(item.modelId);
        // Debug: Log what's being filtered
        if (!isValid) {
          console.log('🚫 Filtering out model:', item.modelId, 'normalized:', normalizeModelId(item.modelId));
        }
        return isValid;
      });
      // Reduced logging frequency - only log when count changes
      // console.log(`📊 Conversation leaderboard: ${beforeFilter} → ${(filtered as any[]).length} models after filtering`);
    } else {
      // In creator mode, DON'T filter by valid OpenRouter models
      // Creator prefixes like "google", "openai", "amazon" are valid if they exist in grades
      // The filtering happens at query time in the hooks - we already have filtered data here
      // Just keep all creators (they're already filtered for qualifications)
      // Reduced logging frequency - only log when count changes
      // console.log(`📊 Conversation leaderboard (creator mode): ${(filtered as typeof conversationCreators).length} creators`);
    }

    // Filter by top10 models/creators for current priority
    // Only apply if priority is set (not custom)
    if (prioritySetting && prioritySetting !== 'custom') {
      if (leaderboardViewMode === 'model') {
        filtered = (filtered as typeof conversationModels).filter(item => 
          isInTop10(item.modelId)
        );
      } else {
        filtered = (filtered as typeof conversationCreators).filter(item => 
          creatorIsInTop10(item.creatorPrefix)
        );
      }
    }

    return filtered;
  }, [conversationData, shouldHideIonoffInConversationLeaderboard, leaderboardViewMode, conversationCreators, conversationModels, validOpenRouterModelIds, prioritySetting, isInTop10, creatorIsInTop10]);

  // Filter user and global data to only show valid OpenRouter models
  const filteredUserData = useMemo(() => {
    console.log('📊 ChatSidebar: Filtering user data', {
      leaderboardViewMode,
      userDataCount: userData.length,
      validOpenRouterModelIdsCount: validOpenRouterModelIds.size,
      prioritySetting
    });
    
    let filtered;
    
    if (leaderboardViewMode === 'model') {
      filtered = (userData as typeof userModels).filter(item => {
        const isValid = item.modelId &&
        isValidOpenRouterModel(item.modelId) &&
        !item.modelId.startsWith('ionoff-grading') &&
          item.modelId !== 'best';
        
        if (!isValid && item.modelId) {
          console.log('🚫 ChatSidebar: Filtering out model', {
            modelId: item.modelId,
            isValidOpenRouter: isValidOpenRouterModel(item.modelId),
            startsWithIonoffGrading: item.modelId.startsWith('ionoff-grading'),
            isBest: item.modelId === 'best'
          });
        }
        
        return isValid;
      });
      
      console.log('📊 ChatSidebar: Filtered user data result', {
        originalCount: (userData as typeof userModels).length,
        filteredCount: filtered.length,
        modelIds: filtered.map(m => m.modelId)
      });
    } else {
      // In creator mode, filter out synthetic ionoff-grading and best
      filtered = (userData as typeof userCreators).filter(item =>
        item.creatorPrefix &&
        !item.creatorPrefix.startsWith('ionoff-grading') &&
        item.creatorPrefix !== 'best'
      );
    }

    // Filter by top10 models/creators for current priority
    // Only apply if priority is set (not custom)
    if (prioritySetting && prioritySetting !== 'custom') {
      if (leaderboardViewMode === 'model') {
        filtered = (filtered as typeof userModels).filter(item => 
          isInTop10(item.modelId)
        );
      } else {
        filtered = (filtered as typeof userCreators).filter(item => 
          creatorIsInTop10(item.creatorPrefix)
        );
      }
    }
    
    return filtered;
  }, [userData, leaderboardViewMode, userModels, userCreators, prioritySetting, validOpenRouterModelIds, isValidOpenRouterModel, isInTop10, creatorIsInTop10]);

  const filteredGlobalData = useMemo(() => {
    let filtered;
    
    if (leaderboardViewMode === 'model') {
      filtered = (globalData as typeof globalModels).filter(item => {
        // Allow +human to pass through (consolidated human responses)
        if (item.modelId === '+human') {
          return true;
        }
        // Standard filtering for regular models
        return item.modelId &&
          isValidOpenRouterModel(item.modelId) &&
          !item.modelId.startsWith('ionoff-grading') &&
          item.modelId !== 'best';
      });
    } else {
      // In creator mode, filter out synthetic ionoff-grading and best
      filtered = (globalData as typeof globalCreators).filter(item =>
        item.creatorPrefix &&
        !item.creatorPrefix.startsWith('ionoff-grading') &&
        item.creatorPrefix !== 'best'
      );
    }

    // Filter by top10 models/creators for current priority
    // Only apply if priority is set (not custom)
    if (prioritySetting && prioritySetting !== 'custom') {
      if (leaderboardViewMode === 'model') {
        filtered = (filtered as typeof globalModels).filter(item => 
          isInTop10(item.modelId)
        );
      } else {
        filtered = (filtered as typeof globalCreators).filter(item => 
          creatorIsInTop10(item.creatorPrefix)
        );
      }
    }
    
    return filtered;
  }, [globalData, leaderboardViewMode, globalModels, globalCreators, prioritySetting, isInTop10, creatorIsInTop10]);

  // Check which leaderboards have qualified models (after filtering)
  // Use useMemo to ensure this recomputes when thresholds change
  const leaderboardVisibility = useMemo(() => {
    console.log('🎯 Computing leaderboard visibility with thresholds:', thresholds);
    console.log('🔍 Conversation data for visibility check:', {
      count: filteredConversationData.length,
      creators: filteredConversationData.map(c => ({
        creator: ('creatorPrefix' in c) ? c.creatorPrefix : c.modelId,
        judgmentsNumber: c.userJudgment?.judgmentsNumber,
        promptsNumber: c.userJudgment?.promptsNumber
      }))
    });
    return {
      hasConversationLeaderboard: currentConversationId && hasQualifiedModels(filteredConversationData, thresholds.minJudgmentsConversation, false), // N (judgments)
      hasUserLeaderboard: hasQualifiedModels(filteredUserData, thresholds.minPromptsUser, true), // M (prompts)
      hasGlobalLeaderboard: hasQualifiedModels(filteredGlobalData, thresholds.minPromptsGlobal, true), // M (prompts)
    };
  }, [thresholds, currentConversationId, filteredConversationData, filteredUserData, filteredGlobalData]);
  
  const { hasConversationLeaderboard, hasUserLeaderboard, hasGlobalLeaderboard } = leaderboardVisibility;
  
  // Debug: Log leaderboard visibility
  console.log('🔍 Leaderboard visibility:', {
    prioritySetting,
    hasConversationLeaderboard,
    hasUserLeaderboard,
    hasGlobalLeaderboard,
    filteredUserDataCount: filteredUserData.length,
    filteredConversationDataCount: filteredConversationData.length
  });
  
  // Check for self-eval leaderboards separately (they should show even if peer review doesn't qualify)
  const hasConversationSelfEval = conversationSelfEval.length > 0;
  const hasUserSelfEval = userSelfEval.length > 0;
  const hasGlobalSelfEval = globalSelfEval.length > 0;
  
  console.log('🔍 Self-eval leaderboard visibility:', {
    hasConversationSelfEval,
    hasUserSelfEval,
    hasGlobalSelfEval,
    conversationSelfEvalCount: conversationSelfEval.length,
    userSelfEvalCount: userSelfEval.length,
    globalSelfEvalCount: globalSelfEval.length
  });
  
  const hasAnyLeaderboard = hasConversationLeaderboard || hasUserLeaderboard || hasGlobalLeaderboard || hasConversationSelfEval || hasUserSelfEval || hasGlobalSelfEval;
  
  // Check if any leaderboard has more than 3 items (to conditionally show "show 3/all" toggle)
  const anyLeaderboardHasMoreThan3 = 
    conversationSelfEval.length > 3 || 
    userSelfEval.length > 3 || 
    globalSelfEval.length > 3 ||
    conversationData.length > 3 ||
    userData.length > 3 ||
    globalData.length > 3;
  
  console.log('🔍 Final hasAnyLeaderboard:', hasAnyLeaderboard, {
    hasConversationLeaderboard,
    hasUserLeaderboard,
    hasGlobalLeaderboard,
    hasConversationSelfEval,
    hasUserSelfEval,
    hasGlobalSelfEval
  });

  const hasPendingConversation = Boolean(pendingConversationId);
  useEffect(() => {
    console.log('🧪 Pending conversation state:', {
      pendingConversationId,
      conversationCount: conversations.length,
      titles: conversations.slice(0, 3).map(c => ({ id: c.id, title: c.title }))
    });
  }, [pendingConversationId, conversations]);

  // Conversation editing handlers
  const handleStartEdit = (conversation: Conversation, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent conversation selection
    setEditingConversationId(conversation.id);
    setEditingTitle(conversation.title);
  };

  const handleSaveEdit = async (conversationId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (editingTitle.trim() && editingTitle !== conversations.find(c => c.id === conversationId)?.title) {
      try {
        await onRenameConversation(conversationId, editingTitle.trim());
      } catch (error) {
        console.error('Error renaming conversation:', error);
      }
    }
    setEditingConversationId(null);
    setEditingTitle('');
  };

  const handleCancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingConversationId(null);
    setEditingTitle('');
  };

  const handleKeyDown = async (e: React.KeyboardEvent, conversationId: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (editingTitle.trim() && editingTitle !== conversations.find(c => c.id === conversationId)?.title) {
        try {
          await onRenameConversation(conversationId, editingTitle.trim());
        } catch (error) {
          console.error('Error renaming conversation:', error);
        }
      }
      setEditingConversationId(null);
      setEditingTitle('');
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditingConversationId(null);
      setEditingTitle('');
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50
          bg-muted border-r border-border sidebar
          transform transition-transform duration-200 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          flex flex-col
        `}
        style={{ width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px` }}
      >
        {/* Header with close button */}
        <div className="flex items-center justify-end px-4 pt-2 pb-2 lg:hidden">
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggle}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* ionoff Brand - top-justified */}
        {/* 🥚 Easter egg: Click/tap logo 8 times to toggle dev mode */}
        <div className="px-4 pt-3 lg:pt-2 pb-3 border-b border-border">
          <div className="flex items-center justify-center">
            <div 
              className="text-lg cursor-pointer select-none relative"
              onClick={handleLogoClick}
              onTouchEnd={handleLogoTouchEnd}
              title={devModeOverride ? "🥚 Dev mode active (click 8x to disable)" : undefined}
            >
            {tagColors.length > 0 ? (
              <MultiColorIonoff colors={tagColors} size="large" />
            ) : (
              <span className="inline-flex gap-1 items-center" style={{ fontFamily: "'Special Elite', serif" }}>
                <span className="inline-flex items-center justify-center bg-black text-[#ffe119] rounded text-[1.125rem] font-bold" style={{ padding: '0.25rem 0.5rem', minWidth: '1.5rem' }}>i</span>
                <span className="inline-flex items-center justify-center bg-black text-[#3cb44b] rounded text-[1.125rem] font-bold" style={{ padding: '0.25rem 0.5rem', minWidth: '1.5rem' }}>on</span>
                <span className="inline-flex items-center justify-center bg-black text-[#fabed4] rounded text-[1.125rem] font-bold" style={{ padding: '0.25rem 0.5rem', minWidth: '1.5rem' }}>off</span>
              </span>
            )}
            {/* Subtle dev mode indicator - only shown when easter egg is active */}
            {devModeOverride && (
              <span 
                className="absolute -top-1 -right-3 text-[0.5rem] text-yellow-500 opacity-60"
                title="Developer mode active"
              >
                🔧
              </span>
            )}
            </div>
          </div>
        </div>

        {/* Conversation Search — noragsearch-style LLM selection over my conversations */}
        <ConversationSearch
          onSelectConversation={onSelectConversation}
          currentConversationId={currentConversationId}
        />

        {/* New Chat Button */}
        <div className="px-3 py-2">
          <HoverHelpTooltip
            helpText={getHelpText('new conversation') || ''}
            enabled={!shouldShowHelp('new conversation', hasHelp('new conversation')) && hasHelp('new conversation')}
          >
          <Button 
            className="w-full justify-start h-10 rounded-lg" 
            variant="ghost"
            onClick={() => {
              onNewConversation();
              if (hasHelp('new conversation')) markInteracted('new conversation');
            }}
            disabled={hasPendingConversation}
          >
            <Plus className="h-4 w-4 mr-2" />
            new conversation
            {shouldShowHelp('new conversation', hasHelp('new conversation')) && (
              <HelpTooltip
                helpText={getHelpText('new conversation') || ''}
                onHelpViewed={() => markInteracted('new conversation')}
                size="sm"
                className="ml-1"
              />
            )}
          </Button>
          </HoverHelpTooltip>
        </div>
        
        {/* Right2Privacy Button - only show if there are conversations */}
        {conversations.length > 0 && onRight2Privacy && (
          <div className="px-3 relative" style={{ paddingLeft: 'calc(0.75rem + 2.5ch)', marginTop: '-0.5rem', marginBottom: '0' }}>
            <HoverHelpTooltip
              helpText={getHelpText('right2bforgotten') || ''}
              enabled={!shouldShowHelp('right2bforgotten', hasHelp('right2bforgotten')) && hasHelp('right2bforgotten')}
            >
            <Button 
              className="flex-1 justify-start h-7 rounded-lg text-sm" 
              variant="ghost"
              onClick={() => {
                setShowPrivacyConfirm(true);
                // Mark as interacted when clicking the button
                if (hasHelp('right2bforgotten')) markInteracted('right2bforgotten');
              }}
              title="Delete all your conversation data"
              disabled={privacyLoading}
            >
              <Minus className="h-4 w-4 mr-2" />
              right2Bforgotten
              {shouldShowHelp('right2bforgotten', hasHelp('right2bforgotten')) && (
                <HelpTooltip
                  helpText={getHelpText('right2bforgotten') || ''}
                  onHelpViewed={() => markInteracted('right2bforgotten')}
                  size="sm"
                  className="ml-1"
                />
              )}
            </Button>
            </HoverHelpTooltip>
            
            {/* Confirmation Popover */}
            {showPrivacyConfirm && (
              <div className="absolute left-0 top-full mt-1 z-50 bg-background border border-border rounded-lg shadow-lg p-4 w-64">
                <p className="text-sm text-foreground mb-3">
                  Are you sure you want to delete all your conversations? This will replace all your prompts and responses with "right2Bforgotten".
                </p>
                <p className="text-xs text-muted-foreground mb-4">
                  This action cannot be undone.
                </p>
                <div className="flex gap-2 justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPrivacyConfirm(false)}
                    disabled={privacyLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={async () => {
                      setPrivacyLoading(true);
                      try {
                        await onRight2Privacy();
                        setShowPrivacyConfirm(false);
                      } catch (error) {
                        console.error('Right2Privacy failed:', error);
                      } finally {
                        setPrivacyLoading(false);
                      }
                    }}
                    disabled={privacyLoading}
                  >
                    {privacyLoading ? 'Deleting...' : 'Confirm'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Chat History and Leaderboards */}
        <div className="flex-1 overflow-y-auto py-2">
          {/* Chat History */}
          <div className="space-y-1 px-3">
            {/* Recent Title with controls - matching leaderboard style */}
            <div className="flex items-center justify-start gap-2 mb-3">
              {/* Collapse/Expand Chevron */}
              <button
                onClick={() => setRecentExpanded(prev => !prev)}
                className="p-1 hover:bg-primary/10 rounded transition-colors flex-shrink-0"
                title={recentExpanded ? 'Hide recent conversations' : 'Show recent conversations'}
              >
                {recentExpanded ? (
                  <ChevronUp className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                )}
              </button>

              {/* Recent Title */}
              <div className="flex-1 text-left text-sm font-semibold text-foreground">
                my conversations
              </div>

              {/* Show All toggle button - only shown when expanded AND more than 10 items */}
              {recentExpanded && conversations.length > 10 && (
                <button
                  onClick={() => setConversationLimit(prev => prev === 10 ? null : 10)}
                  className={`inline-flex items-center justify-center px-1.5 py-0.5 rounded text-[10px] font-medium
                    transition-all duration-200 cursor-pointer hover:scale-105 bg-gray-600 flex-shrink-0
                    ${conversationLimit === null 
                      ? 'text-white border border-white/70' 
                      : 'text-gray-400 border border-transparent'}`}
                  title={conversationLimit === 10 ? 'Show all conversations' : 'Show first 10 conversations'}
                >
                  <div className="flex flex-col items-center leading-tight">
                    <span>show</span>
                    <span>all</span>
                  </div>
                </button>
              )}
            </div>

            {recentExpanded && (
              <>
                {conversations.length === 0 ? (
                  <div className="text-sm text-muted-foreground text-center py-12">
                    no conversations yet
                  </div>
                ) : (
                  <div className="space-y-0.5">
                    {conversations
                      .filter((conversation) => {
                        // Hide "New Conversation" entries except the currently pending one
                        // This prevents empty conversations from cluttering the recents list
                        if (conversation.title === 'New Conversation') {
                          return conversation.id === pendingConversationId;
                        }
                        return true;
                      })
                      .slice(0, conversationLimit || undefined)
                      .map((conversation) => {
                  const isPending = pendingConversationId === conversation.id && conversation.title === 'New Conversation';
                  const baseTitle = isPending ? 'New Conversation*' : conversation.title;

                  return (
                  <div
                    key={conversation.id}
                    className={`group relative rounded-lg transition-colors ${
                      currentConversationId === conversation.id
                        ? 'bg-muted'
                        : 'hover:bg-muted/50'
                    }`}
                  >
                    {editingConversationId === conversation.id ? (
                      // Edit mode
                      <div className="flex items-center gap-1 px-2 py-1">
                        <input
                          type="text"
                          value={editingTitle}
                          onChange={(e) => setEditingTitle(e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, conversation.id)}
                          className="flex-1 bg-background text-foreground text-sm px-2 py-1 rounded border border-border focus:outline-none focus:ring-1 focus:ring-primary"
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                        />
                        <button
                          onClick={(e) => handleSaveEdit(conversation.id, e)}
                          className="p-1 hover:bg-muted rounded"
                          title="Save"
                        >
                          <Check className="h-4 w-4 text-green-600" />
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="p-1 hover:bg-muted rounded"
                          title="Cancel"
                        >
                          <XCircle className="h-4 w-4 text-muted-foreground" />
                        </button>
                      </div>
                    ) : (
                      // View mode
                      <button
                        onClick={() => onSelectConversation(conversation.id)}
                        className="w-full text-left px-3 py-1 flex items-center justify-between gap-2"
                      >
                        <div className="flex items-center gap-1 flex-1 min-w-0">
                          <span className={`text-sm truncate ${
                            currentConversationId === conversation.id
                              ? 'text-foreground'
                              : 'text-muted-foreground group-hover:text-foreground'
                          }`}>
                            {conversation.batchRunID ? (
                              <>
                                <span className="italic">*</span>{baseTitle}
                              </>
                            ) : (
                              baseTitle
                            )}
                          </span>
                        </div>
                        {/* Publish/Unpublish Button (owner only) */}
                        {/* Visible on hover (desktop) or when conversation is selected (mobile-friendly) */}
                        {onPublishConversation && onUnpublishConversation && (
                          <div
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (conversation.published) {
                                await onUnpublishConversation(conversation.id);
                              } else {
                                await onPublishConversation(conversation.id);
                              }
                            }}
                            className={`
                              p-1 hover:bg-muted-foreground/20 rounded transition-opacity cursor-pointer
                              ${currentConversationId === conversation.id 
                                ? 'opacity-70' 
                                : 'opacity-0 group-hover:opacity-100'}
                            `}
                            title={conversation.published ? 'Unpublish conversation' : 'Publish conversation'}
                            role="button"
                            tabIndex={0}
                          >
                            {conversation.published ? (
                              <EyeOff className="h-3 w-3 text-muted-foreground" />
                            ) : (
                              <Eye className="h-3 w-3 text-muted-foreground" />
                            )}
                          </div>
                        )}
                        {/* Edit Title Button */}
                        {/* Visible on hover (desktop) or when conversation is selected (mobile-friendly) */}
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStartEdit(conversation, e);
                          }}
                          className={`
                            p-1 hover:bg-muted-foreground/20 rounded transition-opacity cursor-pointer
                            ${currentConversationId === conversation.id 
                              ? 'opacity-70' 
                              : 'opacity-0 group-hover:opacity-100'}
                          `}
                          title="Rename conversation"
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.stopPropagation();
                              handleStartEdit(conversation, e as any);
                            }
                          }}
                        >
                          <Pencil className="h-3 w-3 text-muted-foreground" />
                        </div>
                      </button>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
              </>
            )}
          </div>

          {/* Published Conversations Section */}
          <div className="space-y-1 px-3 mt-4">
            {/* Published Title with collapse control */}
            <HoverHelpTooltip
              helpText={getHelpText('published') || ''}
              enabled={!shouldShowHelp('published', hasHelp('published')) && hasHelp('published')}
            >
            <div className="flex items-center justify-start gap-2 mb-3">
              {/* Collapse/Expand Chevron */}
              <button
                onClick={() => {
                  setPublishedExpanded(prev => !prev);
                  // Mark as interacted when expanding/collapsing
                  if (hasHelp('published')) markInteracted('published');
                }}
                className="p-1 hover:bg-primary/10 rounded transition-colors flex-shrink-0"
                title={publishedExpanded ? 'Hide published conversations' : 'Show published conversations'}
              >
                {publishedExpanded ? (
                  <ChevronUp className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                )}
              </button>

              {/* Published Title */}
              <div className="flex-1 text-left text-sm font-semibold text-foreground flex items-center gap-1">
                published
                {shouldShowHelp('published', hasHelp('published')) && (
                  <HelpTooltip
                    helpText={getHelpText('published') || ''}
                    onHelpViewed={() => markInteracted('published')}
                    size="sm"
                  />
                )}
              </div>

              {/* Show All toggle button - only shown when expanded AND more than 10 items */}
              {publishedExpanded && publishedConversations.length > 10 && (
                <button
                  onClick={() => setPublishedLimit(prev => prev === 10 ? null : 10)}
                  className={`inline-flex items-center justify-center px-1.5 py-0.5 rounded text-[10px] font-medium
                    transition-all duration-200 cursor-pointer hover:scale-105 bg-gray-600 flex-shrink-0
                    ${publishedLimit === null 
                      ? 'text-white border border-white/70' 
                      : 'text-gray-400 border border-transparent'}`}
                  title={publishedLimit === 10 ? 'Show all published' : 'Show first 10 published'}
                >
                  <div className="flex flex-col items-center leading-tight">
                    <span>show</span>
                    <span>all</span>
                  </div>
                </button>
              )}
            </div>
            </HoverHelpTooltip>

            {/* Published Conversations List */}
            {publishedExpanded && (
              <div className="space-y-1">
                {publishedConversations.length === 0 ? (
                  <div className="text-xs text-muted-foreground text-center py-2">
                    no published conversations yet
                  </div>
                ) : (
                  publishedConversations.slice(0, publishedLimit === null ? undefined : publishedLimit).map((conversation) => {
                    const isOwner = currentUserId === conversation.userId;
                    const baseTitle = conversation.title || 'Untitled';
                    
                    return (
                      <div
                        key={conversation.id}
                        className={`group relative rounded ${
                          currentConversationId === conversation.id
                            ? 'bg-primary/20'
                            : 'hover:bg-primary/10'
                        }`}
                      >
                        <button
                          onClick={() => onSelectConversation(conversation.id)}
                          className="w-full text-left px-3 py-1 flex items-center justify-between gap-2"
                        >
                          <div className="flex items-center gap-1 flex-1 min-w-0">
                            <span className={`text-sm truncate ${
                              currentConversationId === conversation.id
                                ? 'text-foreground'
                                : 'text-muted-foreground group-hover:text-foreground'
                            }`}>
                              {conversation.batchRunID ? (
                                <>
                                  <span className="italic">*</span>{baseTitle}
                                </>
                              ) : (
                                baseTitle
                              )}
                            </span>
                          </div>
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {/* AI Leaderboard Section (collapsed by default) */}
          {hasAnyLeaderboard && (
            <div className="space-y-1 px-3 mt-4">
              {/* Leaderboard Title with controls */}
              <HoverHelpTooltip
                helpText={getHelpText('leaderboard') || ''}
                enabled={!shouldShowHelp('leaderboard', hasHelp('leaderboard')) && hasHelp('leaderboard')}
              >
              <div className="flex items-center justify-start gap-2 mb-3">
                {/* Collapse/Expand Chevron */}
                <button
                  onClick={() => {
                    setLeaderboardExpanded(prev => !prev);
                    // Mark as interacted when expanding/collapsing
                    if (hasHelp('leaderboard')) markInteracted('leaderboard');
                  }}
                  className="p-1 hover:bg-primary/10 rounded transition-colors flex-shrink-0"
                  title={leaderboardExpanded ? 'Hide leaderboard' : 'Show leaderboard'}
                >
                  {leaderboardExpanded ? (
                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>

                {/* Leaderboard Title - clickable toggle between creator/model view */}
                <button 
                  onClick={() => setLeaderboardViewMode(prev => prev === 'creator' ? 'model' : 'creator')}
                  className="flex-1 text-left text-sm font-semibold text-foreground hover:text-primary transition-colors cursor-pointer flex items-center gap-1"
                  title={leaderboardViewMode === 'creator' ? 'Click to show individual models' : 'Click to show AI creators'}
                >
                  {leaderboardViewMode === 'creator' ? 'leaderboard' : 'AI model leaderboard'}
                  {shouldShowHelp('leaderboard', hasHelp('leaderboard')) && (
                    <HelpTooltip
                      helpText={getHelpText('leaderboard') || ''}
                      onHelpViewed={() => markInteracted('leaderboard')}
                      size="sm"
                    />
                  )}
                </button>

                {/* Show All toggle button - only shown when expanded AND more than 3 items */}
                {leaderboardExpanded && anyLeaderboardHasMoreThan3 && (
                  <button
                    onClick={() => setChipLimit(prev => prev === 3 ? null : 3)}
                    className={`inline-flex items-center justify-center px-1.5 py-0.5 rounded text-[10px] font-medium
                      transition-all duration-200 cursor-pointer hover:scale-105 bg-gray-600 flex-shrink-0
                      ${chipLimit === null 
                        ? 'text-white border border-white/70' 
                        : 'text-gray-400 border border-transparent'}`}
                    title={chipLimit === 3 ? 'Show all leaderboard entries' : 'Show first 3 leaderboard entries'}
                  >
                    <div className="flex flex-col items-center leading-tight">
                      <span>show</span>
                      <span>all</span>
                    </div>
                  </button>
                )}
              </div>
              </HoverHelpTooltip>

              {/* Level 1: Conversation-level leaderboards */}
              {leaderboardExpanded && (hasConversationLeaderboard || hasConversationSelfEval) && (
                <div className="mb-4">
                  <div className="text-xs font-medium text-muted-foreground text-center mb-3">
                    this conversation
                  </div>
                  
                  {/* Conversation Self-Eval */}
                  {hasConversationSelfEval && (
                    <div className="mb-0.5">
                      <SelfEvalLeaderboard 
                        judgedModels={conversationSelfEval}
                        tagColors={tagColors}
                        modelColorAssignments={modelColorAssignments}
                        activeModels={activeModels}
                        viewMode={leaderboardViewMode}
                        prioritySetting={prioritySetting}
                        currentUserId={currentUserId}
                        minJudgments={thresholds.minJudgmentsConversation}
                        maxChips={chipLimit}
                      />
                    </div>
                  )}
                  
                  {/* Conversation Peer-Review */}
                  <div className="pb-3 border-b border-border">
                    <MajorityJudgmentLeaderboard 
                      tagColors={tagColors}
                      modelColorAssignments={modelColorAssignments}
                      activeModels={activeModels}
                      judgedModels={filteredConversationData}
                      viewMode={leaderboardViewMode}
                      minJudgments={thresholds.minJudgmentsConversation}
                      prioritySetting={prioritySetting}
                      currentUserId={currentUserId}
                      maxChips={chipLimit}
                      onModelClick={(modelId) => {
                        console.log('Model clicked in conversation leaderboard:', modelId);
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Level 2: User-level leaderboards */}
              {leaderboardExpanded && (() => {
                const shouldRender = hasUserLeaderboard || hasUserSelfEval;
                console.log('🔍 [RENDER CHECK] hasUserLeaderboard:', hasUserLeaderboard, 'hasUserSelfEval:', hasUserSelfEval, 'Will render:', shouldRender);
                return shouldRender;
              })() && (
                <div className="mb-4">
                  <div className="text-xs font-medium text-muted-foreground text-center mb-3">
                    your conversations
                  </div>
                  
                  {/* User Self-Eval */}
                  {userSelfEval.length > 0 && (
                    <div className="mb-0.5">
                      <SelfEvalLeaderboard 
                        judgedModels={userSelfEval}
                        tagColors={tagColors}
                        modelColorAssignments={modelColorAssignments}
                        activeModels={activeModels}
                        viewMode={leaderboardViewMode}
                        prioritySetting={prioritySetting}
                        currentUserId={currentUserId}
                        minJudgments={thresholds.minPromptsUser}
                        maxChips={chipLimit}
                      />
                    </div>
                  )}
                  
                  {/* User Peer-Review */}
                  <div className="pb-3 border-b border-border">
                    <MajorityJudgmentLeaderboard 
                      tagColors={tagColors}
                      modelColorAssignments={modelColorAssignments}
                      activeModels={activeModels}
                      judgedModels={filteredUserData}
                      viewMode={leaderboardViewMode}
                      minJudgments={thresholds.minPromptsUser}
                      prioritySetting={prioritySetting}
                      currentUserId={currentUserId}
                      maxChips={chipLimit}
                      onModelClick={(modelId) => {
                        console.log('Model clicked in user leaderboard:', modelId);
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Level 3: Global leaderboards */}
              {leaderboardExpanded && (hasGlobalLeaderboard || hasGlobalSelfEval) && (
                <div className="mb-4">
                  <div className="text-xs font-medium text-muted-foreground text-center mb-3">
                    all conversations
                  </div>
                  
                  {/* Global Self-Eval */}
                  {globalSelfEval.length > 0 && (
                    <div className="mb-0.5">
                      <SelfEvalLeaderboard 
                        judgedModels={globalSelfEval}
                        tagColors={tagColors}
                        modelColorAssignments={modelColorAssignments}
                        activeModels={activeModels}
                        viewMode={leaderboardViewMode}
                        prioritySetting={prioritySetting}
                        currentUserId={currentUserId}
                        minJudgments={thresholds.minPromptsGlobal}
                        maxChips={chipLimit}
                      />
                    </div>
                  )}
                  
                  {/* Global Peer-Review */}
                  <div className="pb-3 border-b border-border">
                    <MajorityJudgmentLeaderboard 
                      tagColors={tagColors}
                      modelColorAssignments={modelColorAssignments}
                      activeModels={activeModels}
                      judgedModels={filteredGlobalData}
                      viewMode={leaderboardViewMode}
                      minJudgments={thresholds.minPromptsGlobal}
                      prioritySetting={prioritySetting}
                      currentUserId={currentUserId}
                      maxChips={chipLimit}
                      onModelClick={(modelId) => {
                        console.log('Model clicked in global leaderboard:', modelId);
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

